import React, { useEffect, useRef, useState } from "react";
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { FormulaValidationResult, ReportConfig } from "./types";
import { useReportBuilderStore } from "./store";

// ─── Supported functions ──────────────────────────────────────────────────────

const EXPR_FUNCTIONS = [
  {
    name: "IF",
    // The text inserted when the user clicks the row
    insert: "IF(",
    sig: "IF(condition, value_if_true, value_if_false)",
    ex: "IF({vested} > 0, {vested}, 0)",
    hint: "Returns one of two values depending on whether the condition is true.",
  },
  {
    name: "ROUND",
    insert: "ROUND(",
    sig: "ROUND(value, decimals)",
    ex: "ROUND({strike_price}, 2)",
    hint: "Rounds a number to the given number of decimal places.",
  },
  {
    name: "CONCAT",
    insert: "CONCAT(",
    sig: "CONCAT(a, b, ...)",
    ex: 'CONCAT({employee_name}, " - ", {plan_name})',
    hint: "Joins multiple text values into one string.",
  },
  {
    name: "DATE_DIFF",
    insert: "DATE_DIFF(",
    sig: 'DATE_DIFF(date1, date2, "days"|"months"|"years")',
    ex: 'DATE_DIFF({vesting_start_date}, {grant_date}, "months")',
    hint: 'Returns the difference between two dates in the specified unit ("days", "months", or "years").',
  },
  {
    name: "COALESCE",
    insert: "COALESCE(",
    sig: "COALESCE(a, b, ...)",
    ex: 'COALESCE({designation}, "N/A")',
    hint: "Returns the first non-empty value from the list.",
  },
  {
    name: "ABS",
    insert: "ABS(",
    sig: "ABS(value)",
    ex: "ABS({outstanding})",
    hint: "Returns the absolute (positive) value of a number.",
  },
  {
    name: "MIN",
    insert: "MIN(",
    sig: "MIN(a, b)",
    ex: "MIN({vested}, {granted_options})",
    hint: "Returns the smaller of two values.",
  },
  {
    name: "MAX",
    insert: "MAX(",
    sig: "MAX(a, b)",
    ex: "MAX({vested} - {exercised_total}, 0)",
    hint: "Returns the larger of two values. Often used to floor a result at 0.",
  },
  {
    name: "NOW",
    insert: "NOW()",
    sig: "NOW()",
    ex: 'DATE_DIFF(NOW(), {grant_date}, "days")',
    hint: "Returns the current date and time at the moment the report is generated.",
  },
];

// ─── Validation ───────────────────────────────────────────────────────────────

const KNOWN_FNS = new Set([
  "IF",
  "ROUND",
  "CONCAT",
  "DATE_DIFF",
  "COALESCE",
  "ABS",
  "MIN",
  "MAX",
  "SUM_IF",
  "NOW",
]);

export function validateFormula(
  formula: string,
  availableCols: string[]
): FormulaValidationResult {
  if (!formula.trim())
    return { valid: false, error: "Expression cannot be empty" };

  let depth = 0;
  for (const ch of formula) {
    if (ch === "(") depth++;
    if (ch === ")") depth--;
    if (depth < 0)
      return { valid: false, error: "Unexpected closing parenthesis )" };
  }
  if (depth > 0)
    return { valid: false, error: `Missing ${depth} closing parenthesis )` };

  if (
    (formula.match(/\{/g) ?? []).length !== (formula.match(/\}/g) ?? []).length
  ) {
    return {
      valid: false,
      error: "Unclosed column reference: add a closing }",
    };
  }

  const refs = [...formula.matchAll(/\{([^}]+)\}/g)].map((m) => m[1]);
  const missing = refs.filter(
    (ref) => !availableCols.some((c) => c.toLowerCase() === ref.toLowerCase())
  );
  if (missing.length > 0) {
    return {
      valid: false,
      error: `Column not found in this sheet: "${missing[0]}"`,
      warnings:
        missing.length > 1
          ? [`Also not found: ${missing.slice(1).join(", ")}`]
          : undefined,
    };
  }

  const fnCalls = [...formula.matchAll(/\b([A-Z_]{2,})\s*\(/g)].map(
    (m) => m[1]
  );
  const unknown = fnCalls.filter((f) => !KNOWN_FNS.has(f));
  if (unknown.length > 0) {
    return { valid: false, error: `Unknown function: ${unknown[0]}()` };
  }

  if (/\/\s*0(?!\.)/.test(formula)) {
    return {
      valid: true,
      warnings: [
        "Divides by literal 0. Wrap in IF() to handle zero values safely.",
      ],
    };
  }

  return { valid: true };
}

export function validateReportFormulas(config: ReportConfig): string | null {
  for (const sheet of config.sheets) {
    for (const column of sheet.columns.filter((item) => item.isCustom)) {
      const availableKeys = sheet.columns
        .filter((item) => item.id !== column.id)
        .map((item) => item.sourceColumn || item.displayName);
      const validation = validateFormula(column.formula ?? "", availableKeys);
      if (!validation.valid) {
        return `Fix the formula in “${column.displayName}”: ${validation.error}`;
      }
    }
  }
  return null;
}

// ─── Autocomplete helpers ─────────────────────────────────────────────────────

function getActiveRefToken(value: string, cursorPos: number): string | null {
  const before = value.slice(0, cursorPos);
  const lastOpen = before.lastIndexOf("{");
  if (lastOpen === -1) return null;
  const afterOpen = before.slice(lastOpen + 1);
  if (afterOpen.includes("}")) return null;
  return afterOpen;
}

// ─── FormulaEditor ────────────────────────────────────────────────────────────

interface FormulaEditorProps {
  value: string;
  onChange: (v: string) => void;
  columnId: string;
}

export function FormulaEditor({
  value,
  onChange,
  columnId,
}: FormulaEditorProps) {
  const { config, activeSheetId } = useReportBuilderStore();
  const activeSheet =
    config.sheets.find((s) => s.id === activeSheetId) ?? config.sheets[0];

  const [showRef, setShowRef] = useState(false);
  const [validation, setValidation] = useState<FormulaValidationResult | null>(
    null
  );
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [suggestionIdx, setSuggestionIdx] = useState(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Columns that are already on the canvas (excluding this custom column itself).
  // These are the ONLY columns that can be referenced in the expression.
  const siblingCols = activeSheet.columns.filter((c) => c.id !== columnId);
  const availableKeys = siblingCols.map((c) => c.sourceColumn || c.displayName);

  // Debounced validation
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value.trim()) {
      setValidation(null);
    } else {
      debounceRef.current = setTimeout(() => {
        setValidation(validateFormula(value, availableKeys));
      }, 350);
    }
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [value]);

  // Autocomplete: recompute on value / cursor change
  function updateSuggestions(val: string, cursor: number) {
    const partial = getActiveRefToken(val, cursor);
    if (partial === null) {
      setSuggestions([]);
      return;
    }
    const lower = partial.toLowerCase();
    const matches = availableKeys.filter((k) =>
      k.toLowerCase().includes(lower)
    );
    setSuggestions(matches);
    setSuggestionIdx(0);
  }

  function handleChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    onChange(e.target.value);
    updateSuggestions(
      e.target.value,
      e.target.selectionStart ?? e.target.value.length
    );
  }

  function handleSelect(e: React.SyntheticEvent<HTMLTextAreaElement>) {
    const el = e.currentTarget;
    updateSuggestions(el.value, el.selectionStart ?? el.value.length);
  }

  /**
   * Insert text at the current cursor position, keeping focus + cursor placement.
   * Uses onMouseDown + preventDefault pattern so the textarea never loses focus.
   */
  function insertAtCursor(text: string, cursorOffsetFromEnd = 0) {
    const el = textareaRef.current;
    if (!el) {
      onChange(value + text);
      return;
    }
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    const next = value.slice(0, start) + text + value.slice(end);
    onChange(next);
    setSuggestions([]);
    const newCursor = start + text.length - cursorOffsetFromEnd;
    setTimeout(() => {
      el.focus();
      el.setSelectionRange(newCursor, newCursor);
    }, 0);
  }

  // For column chip quick-insert: wraps key in braces
  function insertRef(key: string) {
    insertAtCursor(`{${key}}`);
  }

  // For function row click: inserts e.g. "ROUND(" and places cursor inside parens
  function insertFn(fn: typeof EXPR_FUNCTIONS[0]) {
    // For NOW() insert the whole thing; for others insert "NAME(" — cursor goes inside
    if (fn.insert.endsWith(")")) {
      insertAtCursor(fn.insert);
    } else {
      // insert "NAME()" with cursor between the parens
      insertAtCursor(`${fn.insert})`, 1);
    }
  }

  function applySuggestion(key: string) {
    const el = textareaRef.current;
    if (!el) return;
    const cursor = el.selectionStart ?? value.length;
    const partial = getActiveRefToken(value, cursor);
    if (partial === null) return;

    const openPos = value.slice(0, cursor).lastIndexOf("{");
    const before = value.slice(0, openPos);
    const afterCursor = value.slice(cursor);
    const hasClose = afterCursor.startsWith("}");
    const after = hasClose ? afterCursor.slice(1) : afterCursor;

    const next = `${before}{${key}}${after}`;
    onChange(next);
    setSuggestions([]);

    const newCursor = openPos + key.length + 2;
    setTimeout(() => {
      el.focus();
      el.setSelectionRange(newCursor, newCursor);
    }, 0);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSuggestionIdx((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSuggestionIdx(
        (i) => (i - 1 + suggestions.length) % suggestions.length
      );
    } else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      applySuggestion(suggestions[suggestionIdx]);
    } else if (e.key === "Escape") {
      setSuggestions([]);
    }
  }

  return (
    <div className="flex flex-col gap-3">
      {/* ── Info banner ── */}
      <div className="flex items-start gap-1.5 px-2.5 py-2 bg-blue-50 border border-blue-200 rounded-lg text-[11px] text-blue-700 leading-snug">
        <InformationCircleIcon className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
        <span>
          Evaluated <strong>server-side</strong> when the report is generated,
          not an Excel formula. You can only reference{" "}
          <strong>columns already added to this sheet</strong>. Only the
          functions listed below are supported.
        </span>
      </div>

      {/* ── No sibling columns warning ── */}
      {siblingCols.length === 0 && (
        <div className="flex items-start gap-1.5 px-2.5 py-2 bg-amber-50 border border-amber-200 rounded-lg text-[11px] text-amber-700 leading-snug">
          <ExclamationTriangleIcon className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <span>
            No other columns are on this sheet yet. Add columns from the schema
            browser first. Only columns on this sheet can be referenced in
            expressions.
          </span>
        </div>
      )}

      {/* ── Textarea + autocomplete ── */}
      <div className="relative">
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Expression
        </label>
        <textarea
          ref={textareaRef}
          rows={3}
          value={value}
          onChange={handleChange}
          onSelect={handleSelect}
          onKeyDown={handleKeyDown}
          onBlur={() => setTimeout(() => setSuggestions([]), 150)}
          placeholder="e.g. ROUND({vested} / {granted_options} * 100, 1)"
          spellCheck={false}
          className={`w-full text-xs font-mono px-2.5 py-2 border rounded-md focus:outline-none focus:ring-1 resize-none bg-gray-50 ${
            validation && !validation.valid
              ? "border-red-300 focus:ring-red-300"
              : validation?.valid
              ? "border-green-300 focus:ring-green-300"
              : "border-gray-200 focus:ring-primary"
          }`}
        />

        {/* Autocomplete dropdown */}
        {suggestions.length > 0 && (
          <div className="absolute z-50 left-0 right-0 mt-0.5 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden max-h-44 overflow-y-auto">
            {suggestions.map((key, i) => (
              <button
                key={key}
                type="button"
                // onMouseDown + preventDefault keeps textarea focused so selectionStart is correct
                onMouseDown={(e) => {
                  e.preventDefault();
                  applySuggestion(key);
                }}
                className={`w-full text-left px-3 py-1.5 text-xs font-mono transition-colors ${
                  i === suggestionIdx
                    ? "bg-activeColor text-primary font-semibold"
                    : "hover:bg-gray-50 text-gray-700"
                }`}
              >
                <span className="text-gray-400">{`{`}</span>
                {key}
                <span className="text-gray-400">{`}`}</span>
              </button>
            ))}
          </div>
        )}

        <p className="text-[10px] text-gray-400 mt-0.5">
          Type <span className="font-mono text-primary">{"{"}</span> to start a
          column reference. Autocomplete will appear.
        </p>
      </div>

      {/* ── Validation feedback ── */}
      {validation && (
        <div
          className={`flex items-start gap-1.5 text-[11px] px-2.5 py-2 rounded-lg border ${
            validation.valid
              ? "bg-green-50 border-green-200 text-green-700"
              : "bg-red-50 border-red-200 text-red-700"
          }`}
        >
          {validation.valid ? (
            <CheckCircleIcon className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          ) : (
            <ExclamationCircleIcon className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          )}
          <div>
            <span>
              {validation.valid ? "Valid expression" : validation.error}
            </span>
            {validation.warnings?.map((w, i) => (
              <div key={i} className="text-amber-600 mt-0.5">
                ⚠ {w}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Column quick-insert chips ── */}
      {siblingCols.length > 0 && (
        <div>
          <p className="text-[10px] font-medium text-gray-500 mb-1.5">
            Columns on this sheet (click to insert):
          </p>
          <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto">
            {siblingCols.map((col) => {
              const key = col.sourceColumn || col.displayName;
              return (
                <button
                  key={col.id}
                  type="button"
                  // onMouseDown + preventDefault preserves textarea focus and cursor position
                  onMouseDown={(e) => {
                    e.preventDefault();
                    insertRef(key);
                  }}
                  title={col.description ?? col.displayName}
                  className="text-[10px] px-1.5 py-0.5 bg-white border border-gray-200 rounded hover:bg-activeColor hover:border-primary hover:text-primary transition-colors font-mono"
                >
                  {`{${key}}`}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Function reference — click any row to insert ── */}
      <div>
        <button
          type="button"
          onClick={() => setShowRef((v) => !v)}
          className="flex items-center gap-1 text-[11px] text-primary hover:underline"
        >
          {showRef ? (
            <ChevronUpIcon className="w-3 h-3" />
          ) : (
            <ChevronDownIcon className="w-3 h-3" />
          )}
          {showRef ? "Hide" : "Show"} supported functions
        </button>

        {showRef && (
          <>
            <p className="text-[10px] text-gray-400 mt-1 mb-1.5">
              Click any function to insert it at the cursor.
            </p>
            <div className="border border-gray-200 rounded-lg overflow-hidden divide-y divide-gray-100 text-[11px]">
              {EXPR_FUNCTIONS.map((fn) => (
                <button
                  key={fn.name}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    insertFn(fn);
                  }}
                  className="w-full text-left px-3 py-2 bg-white hover:bg-activeColor hover:border-primary transition-colors group"
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="font-mono font-semibold text-secondary group-hover:text-primary">
                      {fn.sig}
                    </span>
                    <span className="text-[10px] text-primary font-medium opacity-0 group-hover:opacity-100 flex-shrink-0">
                      Insert ↵
                    </span>
                  </div>
                  <div className="text-gray-400 mt-0.5 leading-snug">
                    {fn.hint}
                  </div>
                  <div className="font-mono text-gray-300 mt-0.5">
                    e.g. {fn.ex}
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
